import { type PayloadRichText, API_BASE_URL } from '../data/posts';

type LexicalNode = {
  type: string;
  tag?: string;
  text?: string;
  format?: number | string;
  children?: LexicalNode[];
  listType?: string;
  value?: number | string | UploadValue;
  relationTo?: string;
  fields?: Record<string, unknown>;
  direction?: string;
  indent?: number;
  version?: number;
  [key: string]: unknown;
};

type UploadValue = {
  id?: number | string;
  url?: string;
  alt?: string;
  width?: number;
  height?: number;
  mimeType?: string;
  filename?: string;
};

const buildMediaUrl = (path: string): string => {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  try {
    return new URL(path, API_BASE_URL).toString();
  } catch {
    return path;
  }
};

const TextFormatFlags = {
  BOLD: 1,
  ITALIC: 2,
  STRIKETHROUGH: 4,
  UNDERLINE: 8,
  CODE: 16,
  SUBSCRIPT: 32,
  SUPERSCRIPT: 64,
} as const;

const renderTextNode = (node: LexicalNode, key: string) => {
  const text = node.text ?? '';
  if (!text) return null;

  const format = typeof node.format === 'number' ? node.format : 0;
  let element: React.ReactNode = text;

  if (format & TextFormatFlags.CODE) {
    element = <code>{element}</code>;
  }
  if (format & TextFormatFlags.BOLD) {
    element = <strong>{element}</strong>;
  }
  if (format & TextFormatFlags.ITALIC) {
    element = <em>{element}</em>;
  }
  if (format & TextFormatFlags.UNDERLINE) {
    element = <u>{element}</u>;
  }
  if (format & TextFormatFlags.STRIKETHROUGH) {
    element = <s>{element}</s>;
  }
  if (format & TextFormatFlags.SUBSCRIPT) {
    element = <sub>{element}</sub>;
  }
  if (format & TextFormatFlags.SUPERSCRIPT) {
    element = <sup>{element}</sup>;
  }

  return <span key={key}>{element}</span>;
};

const renderUploadNode = (node: LexicalNode, key: string) => {
  const value = node.value;
  if (!value || typeof value !== 'object') return null;

  const upload = value as UploadValue;
  const url = upload.url;
  if (!url) return null;

  const fullUrl = buildMediaUrl(url);
  const alt = upload.alt ?? '';
  const mimeType = upload.mimeType ?? '';

  // Check if it's an image
  if (mimeType.startsWith('image/') || /\.(jpe?g|png|gif|webp|avif|svg|bmp|ico)$/i.test(url)) {
    return (
      <figure key={key} className="richtext-upload">
        <img
          src={fullUrl}
          alt={alt}
          width={upload.width}
          height={upload.height}
          loading="lazy"
          style={{ maxWidth: '100%', height: 'auto' }}
        />
        {alt && <figcaption>{alt}</figcaption>}
      </figure>
    );
  }

  // Non-image uploads render as download links
  return (
    <div key={key} className="richtext-upload richtext-upload--file">
      <a href={fullUrl} target="_blank" rel="noopener noreferrer">
        {upload.filename ?? alt ?? 'Download file'}
      </a>
    </div>
  );
};

const renderRelationshipNode = (node: LexicalNode, key: string) => {
  const value = node.value;
  if (!value || typeof value !== 'object') return null;

  const doc = value as Record<string, unknown>;
  const title = (doc.title ?? doc.name ?? '') as string;

  if (!title) return null;

  return (
    <div key={key} className="richtext-relationship">
      <span>{title}</span>
    </div>
  );
};

const renderNode = (node: LexicalNode, key: string): React.ReactNode => {
  if (node.type === 'text') {
    return renderTextNode(node, key);
  }

  if (node.type === 'linebreak') {
    return <br key={key} />;
  }

  if (node.type === 'tab') {
    return <span key={key}>{'\t'}</span>;
  }

  if (node.type === 'horizontalrule') {
    return <hr key={key} />;
  }

  if (node.type === 'upload') {
    return renderUploadNode(node, key);
  }

  if (node.type === 'relationship') {
    return renderRelationshipNode(node, key);
  }

  const children = node.children?.map((child, i) => renderNode(child, `${key}-${i}`)) ?? [];

  switch (node.type) {
    case 'root':
      return <>{children}</>;

    case 'paragraph': {
      const align = getTextAlign(node.format);
      return (
        <p key={key} style={align ? { textAlign: align } : undefined}>
          {children.length > 0 ? children : <br />}
        </p>
      );
    }

    case 'heading': {
      const Tag = (node.tag as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6') || 'h2';
      const align = getTextAlign(node.format);
      return (
        <Tag key={key} style={align ? { textAlign: align } : undefined}>
          {children}
        </Tag>
      );
    }

    case 'list': {
      const Tag = node.listType === 'number' ? 'ol' : node.listType === 'check' ? 'ul' : 'ul';
      return <Tag key={key}>{children}</Tag>;
    }

    case 'listitem': {
      const checked = node.checked;
      if (typeof checked === 'boolean') {
        return (
          <li key={key} className="richtext-checklist-item">
            <input type="checkbox" checked={checked} readOnly />
            <span>{children}</span>
          </li>
        );
      }
      return <li key={key}>{children}</li>;
    }

    case 'quote': {
      return <blockquote key={key}>{children}</blockquote>;
    }

    case 'link':
    case 'autolink': {
      const fields = node.fields as Record<string, unknown> | undefined;
      const url = (fields?.url as string) ?? (node.url as string) ?? '#';
      const newTab = fields?.newTab ?? node.newTab ?? true;
      const rel = (fields?.rel as string) ?? undefined;
      return (
        <a
          key={key}
          href={url}
          target={newTab ? '_blank' : undefined}
          rel={newTab ? `noopener noreferrer${rel ? ` ${rel}` : ''}` : rel}
        >
          {children}
        </a>
      );
    }

    case 'block': {
      // Custom Payload blocks - render fields if possible
      const fields = node.fields as Record<string, unknown> | undefined;
      if (!fields) return null;

      const blockType = fields.blockType as string;
      const content = fields.content as string | undefined;
      if (content) {
        return (
          <div key={key} className={`richtext-block richtext-block--${blockType ?? 'unknown'}`}>
            {content}
          </div>
        );
      }
      return null;
    }

    default:
      if (children.length > 0) {
        return <div key={key}>{children}</div>;
      }
      return null;
  }
};

const getTextAlign = (format: unknown): React.CSSProperties['textAlign'] | undefined => {
  if (typeof format === 'string') {
    switch (format) {
      case 'left':
      case 'center':
      case 'right':
      case 'justify':
        return format;
      case 'start':
        return 'left';
      case 'end':
        return 'right';
    }
  }
  // Lexical also uses numeric format flags for element alignment
  if (typeof format === 'number' && format > 0) {
    // 1=left, 2=center, 3=right, 4=justify
    switch (format) {
      case 1:
        return 'left';
      case 2:
        return 'center';
      case 3:
        return 'right';
      case 4:
        return 'justify';
    }
  }
  return undefined;
};

type RichTextProps = {
  content: PayloadRichText | null | undefined;
  className?: string;
};

const RichText = ({ content, className }: RichTextProps) => {
  if (!content?.root) {
    return null;
  }

  return <div className={className}>{renderNode(content.root as LexicalNode, 'root')}</div>;
};

export default RichText;
